import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageCircle, Plus, Edit2, Trash2, Archive, RotateCcw, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import TemplateAnalytics from "@/components/TemplateAnalytics";

/**
 * Custom nudge template editor. Platform-admin only. Edits `platform_nudge_templates`
 * in Mongo — new templates instantly become available in the /platform Nudge picker.
 */
export default function NudgeTemplatesPage() {
  const [rows, setRows] = useState([]);
  const [archivedRows, setArchivedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {key, label, body} or null
  const [archiving, setArchiving] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/platform/nudge/templates?include_archived=true").then((r) => {
      const all = r.data.templates || [];
      setRows(all.filter((t) => !t.archived));
      setArchivedRows(all.filter((t) => t.archived));
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    if (!editing?.key || !editing?.body) return toast.error("Key + body are required");
    if (!editing.body.includes("{first}")) return toast.error("Body must include {first} placeholder");
    try {
      await api.post("/platform/nudge/templates", { key: editing.key, label: editing.label, body: editing.body });
      toast.success("Template saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (key) => {
    if (!confirm(`Delete template "${key}"? This can't be undone.`)) return;
    try {
      await api.delete(`/platform/nudge/templates/${key}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const unarchive = async (key) => {
    try {
      await api.post(`/platform/nudge/templates/${key}/unarchive`);
      toast.success(`"${key}" is back in the picker`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Unarchive failed"); }
  };

  const autoArchive = async () => {
    setArchiving(true);
    try {
      const r = await api.post("/platform/nudge/templates/auto-archive");
      const n = r.data.archived_count || 0;
      if (n === 0) {
        toast.success("Nothing to archive — every non-seeded template is still under 20 sends or performing above 5%");
      } else {
        toast.success(`Archived ${n} underperforming template${n === 1 ? "" : "s"}`);
        setArchivedOpen(true);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Auto-archive failed"); }
    finally { setArchiving(false); }
  };

  return (
    <DashboardShell>
      <div className="p-6 max-w-4xl">
        <div className="flex items-center gap-2 mb-1">
          <MessageCircle className="w-4 h-4 text-[hsl(var(--gold))]" strokeWidth={2} />
          <div className="font-mono-label text-[hsl(var(--gold))] text-[0.65rem] tracking-widest">PLATFORM · NUDGE TEMPLATES</div>
        </div>
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-4xl font-bold tracking-tight text-navy" data-testid="nudge-templates-heading">Nudge templates</h1>
            <p className="text-sm text-muted-foreground mt-1">Edit the canned messages that appear in the /platform Nudge picker. Every template must include the <code className="text-navy font-mono bg-muted px-1 rounded">{"{first}"}</code> placeholder — we swap it for the tenant's first name at send time.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={autoArchive}
              disabled={archiving}
              data-testid="nudge-templates-auto-archive-btn"
              title="Archive any non-seeded template with ≥20 sends and <5% reply rate"
            >
              {archiving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Archive className="w-4 h-4 mr-1" />} Auto-archive
            </Button>
            <Button onClick={() => setEditing({ key: "", label: "", body: "Hey {first} — " })} className="bg-navy hover:bg-navy/85" data-testid="nudge-templates-new-btn">
              <Plus className="w-4 h-4 mr-1" /> New template
            </Button>
          </div>
        </div>

        <TemplateAnalytics />

        {loading ? (
          <div className="text-muted-foreground py-12 text-center">Loading…</div>
        ) : (
          <div className="space-y-3">
            {rows.map((t) => (
              <Card key={t.key} className="p-4" data-testid={`nudge-template-card-${t.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-mono-label text-navy">{t.label || t.key}</div>
                      <code className="text-[0.65rem] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{t.key}</code>
                      {t.seeded && <span className="text-[0.6rem] text-emerald-700 bg-emerald-500/10 border border-emerald-500/40 px-1.5 rounded-full">SEED</span>}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{t.preview}</div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ key: t.key, label: t.label, body: t.preview })} data-testid={`nudge-template-edit-${t.key}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(t.key)} data-testid={`nudge-template-delete-${t.key}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
            {rows.length === 0 && <Card className="p-8 text-center text-muted-foreground">No templates yet.</Card>}
          </div>
        )}

        {archivedRows.length > 0 && (
          <Card className="mt-6 overflow-hidden" data-testid="archived-templates-section">
            <button
              type="button"
              onClick={() => setArchivedOpen((o) => !o)}
              className="w-full px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between hover:bg-muted/50 transition-colors"
              data-testid="archived-templates-toggle"
            >
              <div className="flex items-center gap-2">
                <Archive className="w-4 h-4 text-muted-foreground" />
                <div className="font-display text-lg font-bold">Archived templates</div>
                <span className="text-xs bg-muted text-muted-foreground border border-border rounded-full px-2 py-0.5 font-mono-label tracking-widest">
                  {archivedRows.length}
                </span>
              </div>
              {archivedOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {archivedOpen && (
              <div className="divide-y divide-border">
                {archivedRows.map((t) => (
                  <div key={t.key} className="p-4 flex items-start justify-between gap-3" data-testid={`archived-template-row-${t.key}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-mono-label text-navy line-through decoration-muted-foreground/60">{t.label || t.key}</div>
                        <code className="text-[0.65rem] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{t.key}</code>
                      </div>
                      <div className="text-sm text-muted-foreground/70 mt-1 leading-relaxed line-clamp-2">{t.preview}</div>
                      {t.archived_reason && <div className="text-[0.65rem] font-mono text-red-700 mt-1">{t.archived_reason}</div>}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => unarchive(t.key)} data-testid={`archived-template-unarchive-${t.key}`}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Unarchive
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent data-testid="nudge-template-editor">
            <DialogHeader>
              <DialogTitle>{editing?.key ? `Edit "${editing.key}"` : "New template"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Key (slug)</Label>
                <Input value={editing?.key || ""} onChange={(e) => setEditing({ ...editing, key: e.target.value })} placeholder="renewal_touch" data-testid="nudge-template-key-input" />
              </div>
              <div>
                <Label>Label (display name)</Label>
                <Input value={editing?.label || ""} onChange={(e) => setEditing({ ...editing, label: e.target.value })} placeholder="Renewal touch" data-testid="nudge-template-label-input" />
              </div>
              <div>
                <Label>Body — must include <code className="text-navy bg-muted px-1 rounded">{"{first}"}</code></Label>
                <Textarea rows={5} value={editing?.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })} placeholder="Hey {first} — checking in..." data-testid="nudge-template-body-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} className="bg-navy hover:bg-navy/85" data-testid="nudge-template-save-btn">Save template</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardShell>
  );
}
